export const WhyLivingDonationSection = () => (
    <section className="section stats-section">
        <h2 className="section-title light">Why Living Kidney Donation Makes a Difference</h2>
        <div className="dynamic-inner-wrap dynamic-inner-wrap-narrow">
            <p className="stats-copy">
                Many people do not realize that they may be medically qualified to donate a kidney to one of
                the more than 90,000 currently waiting in the United States. A kidney from a living donor not
                only allows the recipient to undergo a transplant much sooner, but also typically lasts longer
                than kidneys from deceased donors.
            </p>
        </div>

        <div className="dynamic-inner-wrap dynamic-inner-wrap-narrow stats-grid">
            <article className="stat-card">
                <h3>Living Donation:</h3>
                <div className="metric">
                    <div className="icon calendar"></div>
                    <div>
                        <strong>120 days</strong>
                        <p>
                            Typical wait time for a kidney patient to find a matched kidney from a living donor
                            through the NKR
                        </p>
                    </div>
                </div>
                <div className="metric">
                    <div className="icon hourglass" aria-hidden="true"></div>
                    <div>
                        <strong>20-40 years</strong>
                        <p>Length of time the average kidney from a living donor lasts</p>
                    </div>
                </div>
            </article>
            <article className="stat-card dark">
                <h3>Deceased Donation:</h3>
                <div className="metric">
                    <div className="icon many-calendar" aria-hidden="true"></div>
                    <div>
                        <strong>2-10 years</strong>
                        <p>Typical wait time for a deceased kidney donor</p>
                    </div>
                </div>
                <div className="metric">
                    <div className="icon half-hourglass" aria-hidden="true"></div>
                    <div>
                        <strong>8-15 years</strong>
                        <p>Length of time the average kidney from a deceased donor lasts</p>
                    </div>
                </div>
            </article>
        </div>
    </section>
);

export const WhyDonateThroughNkrSection = () => (
    <section className="section media-section">
        <div className="dynamic-inner-wrap media-grid">
            <div>
                <iframe
                    className="video-card"
                    src="https://player.vimeo.com/video/1143834262?h=c2c754eea1&badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="Donor Mentors"
                ></iframe>
            </div>
            <div className="media-copy">
                <h2>Why Donate through the NKR?</h2>
                <p>
                    The National Kidney Registry provides a range of programs and protections designed to make
                    donation as safe, convenient, and positive as possible. Learn more about donor protections
                    at{' '}
                    <a href="https://www.donorshield.com/kidney-donors/" target="_blank" rel="noreferrer">
                        www.donorshield.com
                    </a>
                    .
                </p>
                <p>
                    For more information, see{' '}
                    <a
                        href="https://www.kidneyregistry.com/for-donors/i-want-to-learn-more-about-living-kidney-donation/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        What is Living Kidney Donation?
                    </a>
                </p>
            </div>
        </div>
    </section>
);

export const WhatIsInvolvedSection = () => (
    <section className="section process-section">
        <h2 className="section-title light">What is Involved in Donating A Kidney?</h2>
        <div className="dynamic-inner-wrap dynamic-inner-wrap-narrow">
            <p className="process-copy">
                Donating a kidney is a life-changing gift but also a major commitment that involves extensive
                testing, major surgery and weeks of recovery time. If you decide to donate a kidney, here&apos;s
                the process you&apos;ll go through.
            </p>
        </div>

        <div className="dynamic-inner-wrap dynamic-inner-wrap-narrow process-grid">
            <article className="process-card">
                <div className="icon clipboard" aria-hidden="true"></div>
                <h3>Screening &amp; Testing</h3>
                <hr className="red-line" />
                <ul className="process-list">
                    <li className="bullet one">
                        <span className="process">Complete a confidential screening / medical history</span>
                        <i className="icon timer"></i>
                        <span className="process-stat">45 mins</span>
                    </li>
                    <li className="bullet two">
                        <span className="process">Complete pre-workup labs*</span>
                        <i className="icon timer"></i>
                        <span className="process-stat">3-4 weeks</span>
                    </li>
                    <li className="bullet three">
                        <span className="process">Center will contact you</span>
                        <i className="icon calendar-week"></i>
                        <span className="process-stat">5 days</span>
                    </li>
                    <li className="bullet four">
                        <span className="process">Complete standard workup</span>
                        <i className="icon calendar-day"></i>
                        <span className="process-stat">3 days</span>
                    </li>
                    <li className="bullet five">
                        <span className="process">Get cleared for donation</span>
                        <i className="icon calendar-blank"></i>
                        <span className="process-stat">1-4 months</span>
                    </li>
                </ul>
            </article>

            <article className="process-card">
                <div className="icon hospital" aria-hidden="true"></div>
                <h3>Surgery &amp; Recovery</h3>
                <hr className="red-line" />
                <ul className="process-list">
                    <li className="surgery-recovery">
                        <span className="process">Complete pre-op</span>
                        <i className="icon calendar-day"></i>
                        <span className="process-stat">1 day</span>
                    </li>
                    <li className="surgery-recovery">
                        <span className="process">Complete surgery</span>
                        <i className="icon timer"></i>
                        <span className="process-stat">1-5 hours</span>
                    </li>
                    <li className="surgery-recovery">
                        <span className="process">Recover in hospital</span>
                        <i className="icon calendar-day"></i>
                        <span className="process-stat">1-4 days</span>
                    </li>
                    <li className="surgery-recovery">
                        <span className="process">Refrain from flying</span>
                        <i className="icon calendar-week"></i>
                        <span className="process-stat">1 week</span>
                    </li>
                    <li className="surgery-recovery">
                        <span className="process">Recover at home**</span>
                        <i className="icon calendar-week"></i>
                        <span className="process-stat">2-6 weeks</span>
                    </li>
                </ul>
            </article>
        </div>

        <p className="process-note">
            *You will not be charged for any pre-workup labs related to your donation.
            <br />
            **Most people can return to normal activities after 2-4 weeks. Donors with physically demanding
            jobs may need 4-6 weeks of recovery before returning to work. High-performance athletes will need
            6 months to a year before they are back to pre-donation performance levels.
        </p>
    </section>
);
